import React from 'react';
import styles from './Input.module.css';

/**
 * Input Component
 *
 * A flexible input component with support for labels, errors, and various types.
 *
 * @param {Object} props
 * @param {string} props.label - Input label
 * @param {string} props.error - Error message
 * @param {string} props.helperText - Helper text below input
 * @param {'sm' | 'md' | 'lg'} props.size - Input size
 * @param {boolean} props.fullWidth - Full width input
 * @param {React.ReactNode} props.leftIcon - Icon on the left
 * @param {React.ReactNode} props.rightIcon - Icon on the right
 * @param {string} props.className - Additional CSS classes
 *
 * @example
 * <Input
 *   label="Email"
 *   type="email"
 *   placeholder="Enter your email"
 *   error={errors.email}
 * />
 */
const Input = React.forwardRef(({
  label,
  error,
  helperText,
  size = 'md',
  fullWidth = false,
  leftIcon,
  rightIcon,
  className = '',
  id,
  ...props
}, ref) => {
  const inputId = id || `input-${Math.random().toString(36).substr(2, 9)}`;

  const wrapperClassNames = [
    styles.wrapper,
    fullWidth && styles.fullWidth,
    className
  ].filter(Boolean).join(' ');

  const inputClassNames = [
    styles.input,
    styles[size],
    error && styles.error,
    leftIcon && styles.hasLeftIcon,
    rightIcon && styles.hasRightIcon
  ].filter(Boolean).join(' ');

  return (
    <div className={wrapperClassNames}>
      {label && (
        <label htmlFor={inputId} className={styles.label}>
          {label}
        </label>
      )}

      <div className={styles.inputContainer}>
        {leftIcon && (
          <span className={styles.iconLeft}>
            {leftIcon}
          </span>
        )}

        <input
          ref={ref}
          id={inputId}
          className={inputClassNames}
          {...props}
        />

        {rightIcon && (
          <span className={styles.iconRight}>
            {rightIcon}
          </span>
        )}
      </div>

      {error && (
        <span className={styles.errorText}>
          {error}
        </span>
      )}

      {!error && helperText && (
        <span className={styles.helperText}>
          {helperText}
        </span>
      )}
    </div>
  );
});

Input.displayName = 'Input';

export default Input;
